import moment from "moment";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { InvestorTableHeaders, OverviewTableHeaders, SharedTableHeaders } from "../components/molecules/workspaces-table/WorkspacesTableHeader";
import { AuthContext } from "../contexts/AuthContext";
import { DashboardsContext } from "../contexts/DashboardsContext";
import { WorkspacesTableContext, WorkspacesTableFilters } from "../contexts/TablesContext/WorkspacesTableProvider";
import { WorkspacesContext } from "../contexts/WorkspacesContext";
import { getWorkspaceDashboardsFunc } from "../lib/helper";
import { Dashboard, Workspace } from "../types/files";
import { getUniqueElements } from "../utils/getUniqueElements";
import storage from "../utils/storage";

const useWorkspacesTable = (dashboardId?: string, search?: string, sharedOnly?: boolean, summary?: boolean) => {
  const { userGroup } = useContext(AuthContext);
  const { workspaces, acceptedWorkspaces, pendingWorkspaces, workspaceScores, setWorkspacesDashboards } = useContext(WorkspacesContext);
  const { dashboards, externalDashboards, setExternalDashboards } = useContext(DashboardsContext);
    const { loading, sortBy, columns, rows: workspaceArray, filters, hoverAnchor, hoverWorkspace,
      setLoading, setColumns, setRows: setWorkspaceArray, setHoverAnchor, setHoverWorkspace,
      setFilters } = useContext(WorkspacesTableContext);

  const timerRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);

  const sortedWorkspaces = useMemo(() => {
    const dashboardIds = dashboards.map(({id}) => id);
    let sorted: Workspace[] = [...workspaceArray ?? []];

    if (sortBy.includes('Name')) {
      sorted.sort((prev: Workspace, next: Workspace) => prev.name?.localeCompare(next.name));
    } else if (sortBy.includes('Fit score')) {
      sorted = Array.from(new Map(
        sorted.map(workspace => [workspace,
          workspaceScores.find(({workspaceId}) => workspaceId === workspace.id)?.scores
            ?.find(({dashboardId}) => dashboardIds.includes(dashboardId || ''))?.score || 0
        ]) ?? [])
      ).sort((a, b) => b[1] - a[1]).map(entry => entry[0])
    } else if (sortBy.includes('Updated')) {
      return sorted.sort((wA, wB) => moment(wB.updatedAt).diff(moment(wA.updatedAt)));
    } else if (sortBy.includes('Added')) {
      return sorted.sort((wA, wB) => moment(wB.createdAt).diff(moment(wA.createdAt)));
    }

    return sorted;
  }, [dashboards, sortBy, workspaceArray, workspaceScores]);

  const filteredWorkspaces: Workspace[] = useMemo(() => {
    let filtered: Workspace[] = sortedWorkspaces;

    const searchText = (search ?? '').trim().toLowerCase();
    const keywords = searchText.split(',').map(token => token.trim());

    if (keywords.some(keyword => keyword.length > 0)) {
      if (keywords.length === 1) {
        filtered = filtered.filter(workspace =>
          workspace.name?.toLowerCase().includes(searchText) ||
          workspace.email?.toLowerCase().includes(searchText) ||
          workspace.website?.toLowerCase().includes(searchText)
        );
      } else {
        filtered = filtered.filter(workspace => {
          const tags = workspace.investmentThesis?.focusArea?.tags?.map(sector =>sector.toLowerCase()) || [];
          const stages = workspace.investmentThesis?.stage?.stages?.map(stage =>stage.toLowerCase()) || [];
          const regions = workspace.investmentThesis?.geography?.regions?.map(geo =>geo.toLowerCase()) || [];

          return keywords.map(key =>
            [...tags, ...stages, ...regions].map(word => word.includes(key) ? key : null)
          ).flat().filter(getUniqueElements).filter(Boolean)[0];
        });
      }
    }

    if (!!filters.sectors?.length) {
      filtered = filtered.filter(workspace => {
        const tags = workspace.investmentThesis?.focusArea?.tags || [];

        return filters.sectors?.every(key => tags.includes(key));
      });
    }
    if (!!filters.stages?.length) {
      filtered = filtered.filter(workspace => {
        const stages = workspace.investmentThesis?.stage?.stages || [];

        return filters.stages?.every(key => stages.includes(key));
      });
    }
    if (!!filters.geography?.length) {
      filtered = filtered.filter(workspace => {
        const regions = workspace.investmentThesis?.geography?.regions || [];

        return filters.geography?.every(key => regions.includes(key));
      });
    }

    return filtered;
  }, [sortedWorkspaces, search, filters]);

  const handleHover = useCallback((anchor: (HTMLElement|null)[], workspace: Workspace|null) => {
    setHoverAnchor(anchor);
    setHoverWorkspace(workspace);
  }, [setHoverAnchor, setHoverWorkspace]);

  const handleFilterRows = useCallback(({ sectors, stages, geography }: WorkspacesTableFilters) => {
    setFilters(prev => ({
      sectors: sectors ?? prev.sectors,
      stages: stages ?? prev.stages,
      geography: geography ?? prev.geography,
    }));
    handleHover([], null);
  }, [handleHover, setFilters]);

  useEffect(() => {
    if (!!sortBy)
      handleHover([], null);
  // eslint-disable-next-line
  }, [sortBy]);

  useEffect(() => {
    const columnsGroup = storage.getItem('config.workspaces-table-columns') ?? {};

    columnsGroup[userGroup] = sharedOnly ? (summary ? OverviewTableHeaders : SharedTableHeaders) : InvestorTableHeaders;
    storage.setItem('config.workspaces-table-columns', columnsGroup);
    setColumns(columnsGroup[userGroup]);
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      clearTimeout(timerRef.current);
      setWorkspaceArray([...acceptedWorkspaces, ...workspaces]
        .filter(workspace => sharedOnly ? workspace.isShared : !workspace.isShared));
    }, 500);

    return () => {
      clearTimeout(timerRef.current);
    }
  // eslint-disable-next-line
  }, [acceptedWorkspaces, sharedOnly, workspaces]);

  useEffect(() => {
    setLoading(!!timerRef.current && !workspaceArray && !search);
  // eslint-disable-next-line
  }, [workspaceArray, search]);

  useEffect(() => {
    if (sharedOnly && !!acceptedWorkspaces.length) {
      const allWorkspaces = [...pendingWorkspaces, ...acceptedWorkspaces];

      Promise.all(allWorkspaces.map(ws => getWorkspaceDashboardsFunc({ workspaceId: ws.id })))
        .then((collection?: Dashboard[][]) => {
          let updatedWorkspacesDashboards: Record<string, Dashboard[]> = {};

          setWorkspacesDashboards(prev => {
            updatedWorkspacesDashboards = {...prev};

            collection?.forEach((dashboards: Dashboard[], i: number) => {
              if (!updatedWorkspacesDashboards[allWorkspaces[i].id]) {
                updatedWorkspacesDashboards = { ...prev, [allWorkspaces[i].id]: (dashboards ?? []) };
              } else {
                updatedWorkspacesDashboards = { ...prev, [allWorkspaces[i].id]: Array.from(
                  new Map([...(dashboards ?? []), ...prev[allWorkspaces[i].id]].map(dashboard => [dashboard.id, dashboard])).values()
                )};
              }
            });

            return updatedWorkspacesDashboards;
          });
          //
          let externals: Dashboard[] = [...externalDashboards];

          Object.values(updatedWorkspacesDashboards).filter(col => !!col).forEach(data => {
            externals = [...externals, ...((data ?? []) as Dashboard[])]
              .filter((dashboard, i, self) =>
                  i === self.findIndex(d => d.id === dashboard.id)
              );
          });
          setExternalDashboards(externals);
        });
    }
  // eslint-disable-next-line
  }, [sharedOnly, acceptedWorkspaces]);

  return {
    loading,
    columns,
    rows: filteredWorkspaces,
    filters,
    hoverAnchor,
    hoverWorkspace,
    handleFilterRows,
    handleHover,
  };
};

export default useWorkspacesTable;